import { UserClaims } from '@okta/okta-auth-js';
import { atom, AtomOptions, selector } from 'recoil';
import { EmptyEmailObject } from './features/Configuration/components/Emails/fixtures';
import { EmptyUrlObject } from './features/Configuration/components/Urls/fixtures';
import { EmptyWebhookObject } from './features/Configuration/components/Webhook/fixtures';
import { MonitorMetadata } from './features/Monitor/components/AddMonitor/types';
import {
  ConfigurationCTAUrlResponse,
  MessageTypes,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachMetadata,
  OutreachSubscriberConfigResponse,
} from './features/Monitor/types';

import {
  ConfigurationInfoModalAtom,
  ConfirmationModalAtom,
  EmailModalAtom,
  Maybe,
  MonitorCriteriaNameMap,
  OutreachEventLocationsModalAtom,
  Partner,
  UrlModalAtom,
  User,
  WebhookModalAtom,
} from './types';
import { OutreachTypes } from './urls';

export const activeAccountAtom = atom<UserClaims | undefined>({
  key: 'activeAccountAtom',
  default: undefined,
});

export const activePartnerAtom = atom<Partner | undefined>({
  key: 'activePartnerAtom',
  default: undefined,
});

// User or introspect Atom
export const userAtom = atom<Maybe<User>>({
  key: 'userAtom',
  default: undefined,
});

export const locationCountAtom = atom<number | undefined>({
  key: 'locationCountAtom',
  default: undefined,
});

export const usersPartnerListSelector = selector<Maybe<Partner[]>>({
  key: 'usersPartnerListSelector',
  get: ({ get }) => {
    const introspect = get(userAtom);
    let partners: Partner[] = [];
    if (introspect?.partnerIds) {
      partners = Object.entries(introspect.partnerIds).map(([key, obj]) => {
        return { ...{ id: key }, ...obj }; // this is the answer
      });
    }
    return partners;
  },
});

export const confirmationModalAtom = atom<ConfirmationModalAtom>({
  key: 'confirmationModalAtom',
  default: {
    isOpen: false,
  },
});

export const apiVersionAtom = atom<string | null>({
  key: 'apiVersionAtom',
  default: null,
});

export const outreachMetadataAtom = atom<Maybe<OutreachMetadata>>({
  key: 'outreachMetadataAtom',
  default: undefined,
});

export const outreachConfigEmailAtom = atom<Maybe<OutreachConfigResponse[]>>({
  key: 'outreachConfigEmailAtom',
  default: [],
});

export const outreachConfigWebhookAtom = atom<
  Maybe<OutreachConfigResponseWebhook[]>
>({
  key: 'outreachConfigWebhookAtom',
  default: [],
});

export const outreachConfigPushAtom = atom<
  Maybe<OutreachSubscriberConfigResponse[]>
>({
  key: 'outreachConfigPushAtom',
  default: [],
});

export const monitorTimeRangeAtom = atom<number>({
  key: 'monitorTimeRangeAtom',
  default: 7,
});

export const outreachEventLocationsModalAtom = atom<
  OutreachEventLocationsModalAtom
>({
  key: 'outreachEventLocationsModalAtom',
  default: {
    isOpen: false,
  },
});

export const webhookModalAtom = atom<WebhookModalAtom>({
  key: 'webhookModalAtom',
  default: {
    isOpen: false,
    type: 'add',
    webhook: EmptyWebhookObject,
    refetchWebhooks: () => {},
    testWebhook: () => new Promise(() => {}),
  },
});

export const defaultUrlModalAtomValue: AtomOptions<UrlModalAtom> = {
  key: 'urlModalAtom',
  default: {
    isOpen: false,
    type: 'add',
    url: EmptyUrlObject,
    refetchUrls: () => {},
  },
};

export const urlModalAtom = atom<UrlModalAtom>(defaultUrlModalAtomValue);

export const defaultEmailModalAtomValue: AtomOptions<EmailModalAtom> = {
  key: 'emailModalAtom',
  default: {
    isOpen: false,
    type: 'add',
    email: EmptyEmailObject,
    refetchEmails: () => {},
  },
};

export const emailModalAtom = atom<EmailModalAtom>(defaultEmailModalAtomValue);

export const defaultConfigurationInfoModalAtomValue: AtomOptions<ConfigurationInfoModalAtom> = {
  key: 'configurationInfoModalAtom',
  default: {
    isOpen: false,
    type: 'lastError',
    data: undefined,
    monitorType: 'user',
  },
};

export const configurationInfoModalAtom = atom<ConfigurationInfoModalAtom>(
  defaultConfigurationInfoModalAtomValue,
);

type OutreachTestMessageModalAtom = {
  isOpen: boolean;
  messageType: MessageTypes;
  outreachType?: OutreachTypes;
  ctaUrl: Maybe<ConfigurationCTAUrlResponse>;
};

export const outreachTestMessageModalAtom = atom<OutreachTestMessageModalAtom>({
  key: 'outreachTestMessageModalAtom',
  default: {
    isOpen: false,
    messageType: 'issue',
    outreachType: OutreachTypes.Email,
    ctaUrl: undefined,
  },
});

export const selectedCtaUrl = atom<Maybe<ConfigurationCTAUrlResponse>>({
  key: 'selectedCtaUrl',
  default: undefined,
});

export const fetchedMonitorIdsAtom = atom<string[]>({
  key: 'fetchedMonitorIds',
  default: [],
});

export const monitorMetadataAtom = atom<Maybe<MonitorMetadata>>({
  key: 'monitorMetadata',
  default: undefined,
});

export const monitorMetadataLoadingAtom = atom<boolean>({
  key: 'monitorMetadataLoading',
  default: false,
});

export const monitorCriteriaNameSelector = selector<
  Maybe<MonitorCriteriaNameMap>
>({
  key: 'monitorCriteriaNameSelector',
  get: ({ get }) => {
    const metadata = get(monitorMetadataAtom);
    if (!metadata) return undefined;

    const criteriaNames = {
      ...metadata.signalIssues.metadata.specificIssue.allowedValues.reduce(
        (acc, issue) => ({ ...acc, [issue.key]: issue.display }),
        {},
      ),
      health_score: metadata.signalIssues.metadata.healthScore.displayName,

      ...metadata.signalIssues.metadata.issueCategory.allowedValues.reduce(
        (acc, issue) => ({ ...acc, [issue.key]: issue.display }),
        {},
      ),
    };

    return criteriaNames;
  },
});
